/**
 * captureDisplayName — what a capture row is CALLED on screen.
 *
 * WHY THIS FILE EXISTS (Gordon, QA-70, 2026-08-04)
 * "Web Capture is not reflecting the file NAMES captured on mobile — it defaults
 * to the type ('Photo'/'Video'/'Audio'/'Text') instead of the actual file name."
 *
 * MEASURED ROOT CAUSE (2026-08-04) — this is an FE-only gap, not a BE one.
 * The backend's `CaptureItemSerializer` (capture/api/serializers.py) exposes no
 * `file_name` column, but it does not need to: BOTH upload surfaces put the
 * user's original filename INTO the object key.
 *   - web    `utils/s3Helper.uploadFile`  -> `${folder}${token}-${slug(file.name)}`
 *            (the token comes from `utils/uploadObjectKey.buildUniqueObjectKey`)
 *   - mobile `src/api/s3/s3Api.ts:83-84`  -> `${folder}${slugifyFilename(file.name)}`
 *            (no token — a mobile key's basename IS the filename)
 * So `item.media_key` already carries the name on every row the API returns; the
 * web card simply never read it and fell through to the type label.
 *
 * WHAT IT DOES NOT DO
 * It does not invent a name. A text note has no file, and a media item whose key
 * we cannot parse still falls back to its type label — the previous behaviour,
 * kept for those rows rather than showing a raw S3 key.
 */

import { CAPTURE_TYPES, captureTypeLabel } from "./Capture.helpers";

/**
 * The uniqueness prefix `buildUniqueObjectKey` prepends to a web upload's
 * filename: `<base36 timestamp>-<8 chars>-`.
 *
 * Anchored and DELIBERATELY NARROW — it must not eat a real filename that
 * happens to start with hyphenated segments. Both segments are lowercase
 * alphanumeric (base36 / uuid-hex / Math.random base36), the timestamp segment
 * is 6-10 chars (base36 ms since epoch is 8 today and stays 8 until year 2059)
 * and the random segment is exactly 8. Anything else is left alone, which is
 * what makes an untokenised MOBILE key pass through untouched.
 */
const UPLOAD_TOKEN_PREFIX = /^[0-9a-z]{6,10}-[0-9a-z]{8}-(?=.)/;

/**
 * The file name stored inside a capture's `media_key`, or "" when there is none.
 *
 * @param {object} item a capture row from `GET /capture/items/`
 * @returns {string} e.g. `my-holiday-photo.png` — "" for a text note or a
 *   key-less row.
 */
export const captureFileName = (item) => {
  const key = typeof item?.media_key === "string" ? item.media_key.trim() : "";
  if (!key) return "";
  const basename = key.slice(key.lastIndexOf("/") + 1);
  if (!basename) return "";
  return basename.replace(UPLOAD_TOKEN_PREFIX, "");
};

/**
 * The primary label for a capture row.
 *
 * Precedence:
 *  1. `title` (API Title field) — this is the on-screen name.
 *  2. `name` — legacy field name, same meaning as title.
 *  3. file name recovered from `media_key` (QA-70).
 *  4. TEXT only: `text_content` when there is no title (a note has no file).
 *  5. the type label.
 *
 * `text_content` is Notes, not the row title. Media items must never show
 * notes as the primary label.
 */
export const captureDisplayName = (item) => {
  const title =
    (typeof item?.title === "string" && item.title.trim()) ||
    (typeof item?.name === "string" && item.name.trim()) ||
    "";
  if (title) return title;
  if (item?.type !== CAPTURE_TYPES.TEXT) {
    const fileName = captureFileName(item);
    if (fileName) return fileName;
  }
  const note = typeof item?.text_content === "string" ? item.text_content.trim() : "";
  if (item?.type === CAPTURE_TYPES.TEXT && note) return note;
  return captureTypeLabel(item?.type);
};

export const captureHasNotes = (item) =>
  Boolean(typeof item?.text_content === "string" && item.text_content.trim());

